import { useNavigate } from 'react-router-dom'
import useQuery from '@/hooks/useQuery'
import { apiSignIn, apiSignOut, apiSignUp } from '@/features/base/user-management/auth/services/AuthService'
import type { SignInCredential, SignUpCredential } from '@/types/auth'
import { REDIRECT_URL_KEY } from '@/constants/app.constant'
import appConfig from '@/configs/app.config'
import {
    setUser,
    signInSuccess,
    signOutSuccess,
    useAppSelector,
    useAppDispatch,
} from '@/store'
import { resetMtsState } from '@/store/slices/it-process/application-management/mts/mtsSlice'
import { resetNavigationContext } from '@/store/slices/it-process/application-management/mms'
import { resetDetailsTabStripsState } from '@/store/slices/it-process/application-management/dts/detailsTabStripsSlice'
import { resetSDUIState } from '@/store/slices/it-process/application-management/sdui/sduiSlice'
import RtkQueryService from '@/lib/rtkQuery'
import { resetNavigationState } from '@/store/slices/base/navigation/navigationSlice'

// Import the BackendError type from AuthService
import type { BackendError } from '@/features/base/user-management/auth/services/AuthService'
import { businessAdministrationService } from '@/features/base/user-management/business-administration/services/BusinessAdministrationService'

// Extend the BackendError type to include the detail property
interface BackendErrorWithDetail
    extends Omit<BackendError, 'message' | 'statusCode'> {
    detail: string
    message: string // Make message required
    statusCode: number // Make statusCode required to match BackendError
}

function useAuth() {
    const dispatch = useAppDispatch()

    const navigate = useNavigate()

    const query = useQuery()

    const { token, signedIn } = useAppSelector((state) => state.auth.session)

    // Helper function to decode JWT token
    const parseJwt = (token: string) => {
        try {
            const base64Url = token.split('.')[1]
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(
                        (c) =>
                            '%' +
                            ('00' + c.charCodeAt(0).toString(16)).slice(-2),
                    )
                    .join(''),
            )
            return JSON.parse(jsonPayload)
        } catch (e) {
            return null
        }
    }

    interface BackendError {
        message: string
        code: string
        type: string
        statusCode: number
        title: string
        traceId?: string
    }

    interface SignInResponse {
        status: 'success' | 'failed'
        message: string
        error?: BackendError
        statusCode?: number
    }

    const signIn = async (
        values: SignInCredential,
    ): Promise<SignInResponse> => {
        try {
            // apiSignIn will throw an error if login fails
            if (globalThis.navigator.onLine) {
                const signInResult = await apiSignIn(values)
                if (signInResult.status == 200) {
                    // Store the JWT signInResult in localStorage
                    localStorage.setItem('signInResult', signInResult.data)

                    // Decode the JWT signInResult to get user info
                    const decodedToken = parseJwt(signInResult?.data)
                    localStorage.setItem(
                        'signInResult.LanguageId',
                        decodedToken.LanguageId,
                    )

                    // Update auth state with the signInResult string
                    dispatch(signInSuccess(signInResult?.data))

                    // Update user state with info from the signInResult
                    if (decodedToken) {
                        dispatch(
                            setUser({
                                userName:
                                    decodedToken.email?.split('@')[0] || 'User',
                                email: decodedToken.email || '',
                                userId: decodedToken.UserId || '',
                                languageId: decodedToken.LanguageId || '',
                                baId: decodedToken.BA
                                    ? Number(decodedToken.BA)
                                    : 0,
                                authority: ['USER'],
                                expTime: decodedToken.exp
                                    ? decodedToken.exp * 1000 // Store as timestamp in milliseconds
                                    : undefined,
                            }),
                        )
                    }

                    // Redirect to the intended URL or default authenticated path
                    navigate(appConfig.authenticatedEntryPath)

                    return {
                        status: 'success',
                        message: 'Login successful',
                    }
                } else {
                    return {
                        status: 'failed',
                        message: signInResult?.detail,
                    }
                }
            } else {
                return {
                    status: 'failed',
                    message: 'No internet connection',
                }
            }
        } catch (error: unknown) {
            // Default error message
            let errorMessage = 'Failed to sign in. Please try again.'
            let statusCode: number | undefined
            let errorData: BackendErrorWithDetail | undefined

            // Type guard to check if error is an object
            const isErrorWithResponse = (
                e: unknown,
            ): e is {
                response: { data: { detail: string }; status: number }
            } => {
                return (
                    typeof e === 'object' &&
                    e !== null &&
                    'response' in e &&
                    typeof (e as Record<string, unknown>).response ===
                        'object' &&
                    (e as { response: unknown }).response !== null &&
                    'data' in
                        (e as { response: Record<string, unknown> }).response
                )
            }

            // Type guard to check if error has detail property
            const isErrorWithDetail = (
                e: unknown,
            ): e is { detail: string; statusCode?: number } => {
                return (
                    typeof e === 'object' &&
                    e !== null &&
                    'detail' in e &&
                    typeof e.detail === 'string'
                )
            }

            // Type guard to check if error is a rate limit error
            const isRateLimitError = (
                e: unknown,
            ): e is
                | { statusCode: number }
                | { response: { status: number; data?: unknown } } => {
                if (typeof e !== 'object' || e === null) return false

                if ('statusCode' in e && e.statusCode === 429) {
                    return true
                }

                if (
                    'response' in e &&
                    typeof (e as { response: unknown }).response === 'object' &&
                    e.response !== null &&
                    'status' in
                        (e as { response: { status: unknown } }).response &&
                    (e as { response: { status: number } }).response.status ===
                        429
                ) {
                    return true
                }

                return false
            }

            // Extract error details from the error object
            if (isErrorWithResponse(error)) {
                // Handle Axios error response
                const responseData = error.response.data
                errorMessage =
                    'detail' in responseData &&
                    typeof responseData.detail === 'string'
                        ? responseData.detail
                        : errorMessage
                statusCode = error.response.status

                // Only assign if it matches the expected structure
                if (
                    'code' in responseData &&
                    'status' in responseData &&
                    'title' in responseData &&
                    'type' in responseData
                ) {
                    errorData = {
                        code: String(responseData.code),
                        detail:
                            'detail' in responseData &&
                            typeof responseData.detail === 'string'
                                ? responseData.detail
                                : 'An unknown error occurred',
                        status: Number(responseData.status),
                        title: String(responseData.title),
                        type: String(responseData.type),
                        message:
                            'detail' in responseData &&
                            typeof responseData.detail === 'string'
                                ? responseData.detail
                                : 'An unknown error occurred',
                        statusCode: Number(responseData.status),
                        ...('traceId' in responseData && {
                            traceId: String(responseData.traceId),
                        }),
                    }

                    // Use the detail from the error data if available
                    if (errorData.detail) {
                        errorMessage = errorData.detail
                    }
                }
            } else if (isErrorWithDetail(error)) {
                // Handle error with detail property
                errorMessage = error.detail
                statusCode = error.statusCode
            } else if (isRateLimitError(error)) {
                // Handle rate limiting specifically
                let responseData: unknown

                if (
                    'response' in error &&
                    typeof error.response === 'object' &&
                    error.response !== null &&
                    'data' in error.response
                ) {
                    responseData = (error.response as { data: unknown }).data
                }

                const backendMsg =
                    typeof responseData === 'string'
                        ? responseData
                        : 'Too many requests. Please try again later.'
                errorMessage = backendMsg
                statusCode = 429
            }

            return {
                status: 'failed',
                message: errorMessage,
                ...(errorData && { error: errorData }),
                ...(statusCode && { statusCode }),
            }
        }
    }

    const signUp = async (values: SignUpCredential) => {
        try {
            const resp = await apiSignUp(values)
            if (resp.data) {
                const { token } = resp.data
                dispatch(signInSuccess(token))
                if (resp.data.user) {
                    dispatch(
                        setUser(
                            resp.data.user || {
                                avatar: '',
                                userName: 'Anonymous',
                                authority: ['USER'],
                                email: '',
                            },
                        ),
                    )
                }
                const redirectUrl = query.get(REDIRECT_URL_KEY)
                navigate(
                    redirectUrl
                        ? redirectUrl
                        : appConfig.authenticatedEntryPath,
                )
                return {
                    status: 'success',
                    message: '',
                }
            }
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        } catch (errors: any) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const handleSignOut = () => {
        dispatch(signOutSuccess())
        localStorage.removeItem('signInResult')
        businessAdministrationService.handleUserSignOut()
        // Clear all state to prevent showing previous user's data
        dispatch(resetMtsState())
        dispatch(resetNavigationState())
        dispatch(resetNavigationContext())
        dispatch(resetDetailsTabStripsState())
        // Clear cached SDUI screens/edits and the RTK Query data cache so the
        // next user never sees the previous user's screens or form values.
        dispatch(resetSDUIState())
        dispatch(RtkQueryService.util.resetApiState())
        dispatch(
            setUser({
                avatar: '',
                userName: '',
                email: '',
                authority: [],
            }),
        )
        navigate(appConfig.unAuthenticatedEntryPath)
    }

    const signOut = async () => {
        await apiSignOut()
        handleSignOut()
    }

    return {
        authenticated: token && signedIn,
        signIn,
        signUp,
        signOut,
    }
}

export default useAuth
